import { DocumentNode, TypedDocumentNode } from '@apollo/client';
import { path } from 'ramda';

import { graphql } from '@/generated/gql';
import {
  DemandProcessStepsQuery,
  DemandProcessStepsUpdateSubscription,
  NegotiationCaseProcessStepsQuery,
  NegotiationCaseProcessStepsUpdateSubscription,
} from '@/generated/gql/graphql';

import { DEMAND_PROCESS_STEPS_QUERY } from './DemandProcessStepsQuery.gql';
import { DEMAND_PROCESS_STEPS_UPDATE_SUBSCRIPTION } from './DemandProcessStepsUpdateSubscription.gql';
import { NEGOTIATION_CASE_PROCESS_STEPS_QUERY } from './NegotiationCaseProcessStepsQuery.gql';
import { NEGOTIATION_CASE_PROCESS_STEPS_UPDATE_SUBSCRIPTION } from './NegotiationCaseProcessStepsUpdateSubscription.gql';

export enum ProcessStepsAutoRefreshVariant {
  DEMAND_NEGOTIATION_CASE_LIST = 'demandNegotiationCaseList',
  NEGOTIATION_CASE_DETAIL = 'negotiationCaseDetail',
  DEMAND_LIST = 'demandList',
  DEMAND_DETAIL = 'demandDetail',
}

export const CONFIG: Record<ProcessStepsAutoRefreshVariant, { typeName: string; fragment: DocumentNode }> = {
  [ProcessStepsAutoRefreshVariant.DEMAND_NEGOTIATION_CASE_LIST]: {
    typeName: 'TsrctDemandNegotiationCase',
    fragment: graphql(/* GraphQL */ `
      fragment DemandNegotiationCaseProcessSteps on TsrctDemandNegotiationCase {
        isReleasable
        processSteps {
          variant
          status
        }
      }
    `),
  },
  [ProcessStepsAutoRefreshVariant.NEGOTIATION_CASE_DETAIL]: {
    typeName: 'TsrctNegotiationCase',
    fragment: graphql(/* GraphQL */ `
      fragment NegotiationCaseProcessSteps on TsrctNegotiationCase {
        isReleasable
        processSteps {
          variant
          status
        }
      }
    `),
  },
  [ProcessStepsAutoRefreshVariant.DEMAND_LIST]: {
    typeName: 'TsrctDemandListItem',
    fragment: graphql(/* GraphQL */ `
      fragment DemandListItemProcessSteps on TsrctDemandListItem {
        processSteps {
          variant
          status
        }
      }
    `),
  },
  [ProcessStepsAutoRefreshVariant.DEMAND_DETAIL]: {
    typeName: 'TsrctDemand',
    fragment: graphql(/* GraphQL */ `
      fragment DemandDetailProcessSteps on TsrctDemand {
        processSteps {
          variant
          status
        }
      }
    `),
  },
};

export const extractIdFromQuery = <T>(variant: ProcessStepsAutoRefreshVariant, data: T): string | undefined => {
  switch (variant) {
    case ProcessStepsAutoRefreshVariant.DEMAND_LIST:
    case ProcessStepsAutoRefreshVariant.DEMAND_DETAIL:
      return path(['tesseract', 'demandProcessStepsUpdate', 'id'], data);
    case ProcessStepsAutoRefreshVariant.DEMAND_NEGOTIATION_CASE_LIST:
    case ProcessStepsAutoRefreshVariant.NEGOTIATION_CASE_DETAIL:
      return path(['tesseract', 'negotiationCaseProcessStepsUpdate', 'id'], data);
    default:
      return undefined;
  }
};

export const extractDataFromQuery = <T>(variant: ProcessStepsAutoRefreshVariant, data: T): object | undefined => {
  switch (variant) {
    case ProcessStepsAutoRefreshVariant.DEMAND_LIST:
    case ProcessStepsAutoRefreshVariant.DEMAND_DETAIL:
      return { processSteps: path(['tesseract', 'demand', 'processSteps'], data) };

    case ProcessStepsAutoRefreshVariant.DEMAND_NEGOTIATION_CASE_LIST:
    case ProcessStepsAutoRefreshVariant.NEGOTIATION_CASE_DETAIL:
      return {
        isReleasable: path(['tesseract', 'negotiationCase', 'isReleasable'], data),
        processSteps: path(['tesseract', 'negotiationCase', 'processSteps'], data),
      };
    default:
      return undefined;
  }
};

export const getProcessStepsQuery = (
  variant: ProcessStepsAutoRefreshVariant,
): TypedDocumentNode<NegotiationCaseProcessStepsQuery | DemandProcessStepsQuery, { id: string }> | undefined => {
  switch (variant) {
    case ProcessStepsAutoRefreshVariant.DEMAND_LIST:
    case ProcessStepsAutoRefreshVariant.DEMAND_DETAIL:
      return DEMAND_PROCESS_STEPS_QUERY;
    case ProcessStepsAutoRefreshVariant.DEMAND_NEGOTIATION_CASE_LIST:
    case ProcessStepsAutoRefreshVariant.NEGOTIATION_CASE_DETAIL:
      return NEGOTIATION_CASE_PROCESS_STEPS_QUERY;
    default:
      return undefined;
  }
};

export const getProcessStepsUpdateSubscription = (
  variant: ProcessStepsAutoRefreshVariant,
):
  | TypedDocumentNode<
      NegotiationCaseProcessStepsUpdateSubscription | DemandProcessStepsUpdateSubscription,
      { [key in string]: never }
    >
  | undefined => {
  switch (variant) {
    case ProcessStepsAutoRefreshVariant.DEMAND_LIST:
    case ProcessStepsAutoRefreshVariant.DEMAND_DETAIL:
      return DEMAND_PROCESS_STEPS_UPDATE_SUBSCRIPTION;
    case ProcessStepsAutoRefreshVariant.DEMAND_NEGOTIATION_CASE_LIST:
    case ProcessStepsAutoRefreshVariant.NEGOTIATION_CASE_DETAIL:
      return NEGOTIATION_CASE_PROCESS_STEPS_UPDATE_SUBSCRIPTION;
    default:
      return undefined;
  }
};
